import React, { useContext, useState, useRef, useEffect } from 'react';

import { KeyValueView, CustomModal, CustomDrawer, DrawerContext, DataList } from 'commons-ui';
import ModalContext from 'commons-ui/src/context/modal/modalContext';
import usePermission from 'commons-ui/src/permission/usePermission';
import Accordian from 'commons-ui/src/components/keyValueView/Accordian';
import { Button, KIND, SIZE } from 'baseui/button';
import { LIST_ENTITY_NAMES, PAGE_NAMES, ACTION, PAGE_RESOURCE_MAP } from '../../../constant';
import { UserDetailsContext } from '../../../context/userDetails';

import useUserProfileActions from '../useUserProfileActions';
import useBasicDetail from './useBasicDetail';
import { makeActionHandler, makeActionList } from './actions';
import { DETAIL_TYPE_BANK, DETAIL_TYPE_UPI } from './constants';
import { columnConfigsBankDetails, columnConfigsUpiDetails } from './listConfig';
import { makeBankAndUpiActionHandler } from './actions/components/unsetBankAccount/actions/handle';
import { makeBankAndUpiActionList } from './actions/components/unsetBankAccount/actions/list';
import useBankActions from './useBankActions';
import useService from './useService';
import useStyles from './useStyles';
import useContent from './utils/useContent';
import DownloadPayoutDetails from './DownloadPayoutDetails';
import { DownloadableSectionHeaderProps } from '../../../models/basicDetails.model';

const DownloadableSectionHeader: React.FC<DownloadableSectionHeaderProps> = ({
	title,
	onDownloadClick,
	detailType,
	showDownloadButton
}) => {
	const { styledHeaderContainer, styledHeaderTitle, styledDownloadButtonContainer, downloadButtonOverrides } = useStyles();

	return (
		<div className={styledHeaderContainer}>
			<div className={styledHeaderTitle}>{title}</div>
			{showDownloadButton && (
				<div className={styledDownloadButtonContainer} title='download'>
					<Button
						kind={KIND.primary}
						size={SIZE.mini}
						onClick={(event) => onDownloadClick(detailType, event)}
						overrides={downloadButtonOverrides}>
						Download
					</Button>
				</div>
			)}
		</div>
	);
};

const BasicDetails = () => {
	const [downloadSection, setDownloadSection] = useState<typeof DETAIL_TYPE_BANK | typeof DETAIL_TYPE_UPI | null>(null);
	const downloadSectionRef = useRef<typeof DETAIL_TYPE_BANK | typeof DETAIL_TYPE_UPI | null>(null);
	const { styledHeaderTitle } = useStyles();

	useEffect(() => {
		downloadSectionRef.current = downloadSection;
	}, [downloadSection]);

	const {
		selectedUser: { uid }
	} = useContext(UserDetailsContext);
	const { setModalConfig } = useContext(ModalContext);
	const { hasPermission } = usePermission();

	const {
		keyConfig,
		keyValueLayout,
		getBlockUnblockDetails,
		payoutDetailsIsCollapsed,
		setPayoutDetailsIsCollapsed,
		bankDetailsIsCollapsed,
		setBankDetailsIsCollapsed,
		upiDetailsIsCollapsed,
		setUpiDetailsIsCollapsed,
		fetchBankDetails,
		fetchUpiDetails,
		dataFetchComplete,
		forceUpdate,
		setForceUpdate,
		turnForceUpdateOff,
		getUpiAndBankDetails,
		getBasicUserInfo
	} = useBasicDetail();
	const { getActionHandler, getActionList } = useUserProfileActions(makeActionHandler, makeActionList, { getBlockUnblockDetails });
	const { drawerConfig, setDrawerConfig } = useContext(DrawerContext);
	const { getBankAndUpiActionHandler, getBankAndUpiActionList } = useBankActions(
		makeBankAndUpiActionHandler,
		makeBankAndUpiActionList,
		{
			setForceUpdate,
			getUpiAndBankDetails,
			getBasicUserInfo
		}
	);
	const { downloadUserPayoutDetails, downloadLoading } = useService();

	const { handleFormCancel, handleFormSubmit, handleDownloadClick } = useContent({
		setDownloadSection,
		downloadSectionRef,
		downloadUserPayoutDetails,
		uid
	});

	const handleDownloadModal = (section: typeof DETAIL_TYPE_BANK | typeof DETAIL_TYPE_UPI, event: React.SyntheticEvent) => {
		event.stopPropagation();
		handleDownloadClick(section);

		const modalHeader = section === DETAIL_TYPE_BANK ? 'Download Bank Details' : 'Download UPI Details';

		setModalConfig({
			isOpen: true,
			header: modalHeader,
			content: (
				<DownloadPayoutDetails
					loading={downloadLoading}
					onSubmit={async (ticketId: string, description: string) => {
						await handleFormSubmit(ticketId, description);
						setModalConfig({ isOpen: false });
					}}
					onCancel={() => {
						handleFormCancel();
						setModalConfig({ isOpen: false });
					}}
				/>
			)
		});
	};

	const isDownloadPayoutDetailsEnabled = hasPermission({
		configPageName: PAGE_NAMES.userProfile,
		configResourceName: PAGE_RESOURCE_MAP[PAGE_NAMES.userProfile].basicDetails,
		configActionName: ACTION.downloadUserPayoutDetails
	});

	const actions = getBankAndUpiActionList();
	const payoutDetailsLabel = <div className={styledHeaderTitle}>Payout Details</div>;

	const bankDetailsLabel = (
		<DownloadableSectionHeader
			title='Bank Details'
			onDownloadClick={handleDownloadModal}
			detailType={DETAIL_TYPE_BANK}
			showDownloadButton={isDownloadPayoutDetailsEnabled}
		/>
	);

	const upiDetailsLabel = (
		<DownloadableSectionHeader
			title='UPI Details'
			onDownloadClick={handleDownloadModal}
			detailType={DETAIL_TYPE_UPI}
			showDownloadButton={isDownloadPayoutDetailsEnabled}
		/>
	);

	return (
		<>
			<KeyValueView
				keyConfig={keyConfig}
				keyValueLayout={keyValueLayout}
				getActionHandler={getActionHandler}
				getActionList={getActionList}
			/>
			<Accordian
				label={payoutDetailsLabel}
				panelKey='unnamedPanel'
				isCollapsed={payoutDetailsIsCollapsed}
				setIsCollapsed={setPayoutDetailsIsCollapsed}>
				{dataFetchComplete && (
					<>
						<Accordian
							label={bankDetailsLabel}
							panelKey='unnamedPanel'
							isCollapsed={bankDetailsIsCollapsed}
							setIsCollapsed={setBankDetailsIsCollapsed}>
							<DataList
								fetchData={fetchBankDetails}
								columnConfigs={columnConfigsBankDetails}
								getActionHandler={getBankAndUpiActionHandler}
								actions={actions}
								globalForceUpdate={forceUpdate}
								turnGlobalForceUpdateOff={turnForceUpdateOff}
								entity={LIST_ENTITY_NAMES.BANK_AND_UPI_DETAILS}
								filterable={false}
								disablePagination
								searchable={false}
								showFooterMessage={false}
								height='150px'
							/>
						</Accordian>
						<Accordian
							label={upiDetailsLabel}
							panelKey='unnamedPanel'
							isCollapsed={upiDetailsIsCollapsed}
							setIsCollapsed={setUpiDetailsIsCollapsed}>
							<DataList
								fetchData={fetchUpiDetails}
								columnConfigs={columnConfigsUpiDetails}
								getActionHandler={getBankAndUpiActionHandler}
								actions={actions}
								globalForceUpdate={forceUpdate}
								turnGlobalForceUpdateOff={turnForceUpdateOff}
								entity={LIST_ENTITY_NAMES.BANK_AND_UPI_DETAILS}
								filterable={false}
								searchable={false}
								disablePagination
								showFooterMessage={false}
								height='150px'
							/>
						</Accordian>
					</>
				)}
			</Accordian>
			<CustomDrawer drawerConfig={drawerConfig} setDrawerConfig={setDrawerConfig} />
			<CustomModal />
		</>
	);
};

export default BasicDetails;
